import { useMemo, useEffect, useRef, useCallback } from 'react';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  ConnectionMode,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCollide,
  forceX,
  forceY,
  SimulationNodeDatum,
  SimulationLinkDatum,
} from 'd3-force';
import { SimpleCardNode } from './SimpleCardNode';
import { SimpleCircularNode } from './SimpleCircularNode';
import { SimpleEdge } from './SimpleEdge';
import { SaaSNode } from './SaaSNode';
import { SimpleNodeData, NodeType, Provider } from './types';
import type { CloudSaasCorrelation } from '../../types/dashboard';

interface ServiceData {
  service: string;
  secrets: string[];
  nhis?: string[];
  risk: string;
}

interface SaaSData {
  name: string;
  secrets: string[];
  risk: string;
}

interface NHICorrelationGraphProps {
  serviceData?: ServiceData;
  saasData?: SaaSData;
  graphType?: 'service' | 'saas' | 'cloud-saas';
  cloudSaasCorrelation?: CloudSaasCorrelation;
}

interface D3Node extends SimulationNodeDatum {
  id: string;
  isCard: boolean;
}

interface D3Link extends SimulationLinkDatum<D3Node> {
  id: string;
}

const nodeTypes = {
  card: SimpleCardNode,
  circular: SimpleCircularNode,
  saas: SaaSNode,
};

const edgeTypes = {
  simple: SimpleEdge,
};

function buildServiceGraph(serviceData: ServiceData) {
  const nodes: Node<SimpleNodeData>[] = [];
  const edges: Edge[] = [];
  const d3Nodes: D3Node[] = [];
  const d3Links: D3Link[] = [];
  const cx = 400, cy = 200;

  nodes.push({
    id: 'service',
    type: 'card',
    position: { x: cx, y: cy },
    data: {
      label: serviceData.service,
      type: 'service_account' as NodeType,
      provider: 'gcp' as Provider,
      activity: serviceData.risk === 'high' ? 85 : serviceData.risk === 'medium' ? 55 : 30,
    },
  });
  d3Nodes.push({ id: 'service', x: cx, y: cy, isCard: true });

  const radius = 250;
  serviceData.secrets.forEach((secret, i) => {
    const angle = (2 * Math.PI * i) / serviceData.secrets.length - Math.PI / 2;
    const x = cx + radius * Math.cos(angle);
    const y = cy + radius * Math.sin(angle);
    nodes.push({
      id: `secret-${i}`,
      type: 'circular',
      position: { x, y },
      data: { label: secret, type: 'secret' as NodeType },
    });
    d3Nodes.push({ id: `secret-${i}`, x, y, isCard: false });
    edges.push({ id: `e-secret-${i}`, source: 'service', target: `secret-${i}`, type: 'simple' });
    d3Links.push({ id: `e-secret-${i}`, source: 'service', target: `secret-${i}` });
  });

  return { nodes, edges, d3Nodes, d3Links };
}

function buildCloudSaasGraph(corr: CloudSaasCorrelation) {
  const nodes: Node<SimpleNodeData>[] = [];
  const edges: Edge[] = [];
  const d3Nodes: D3Node[] = [];
  const d3Links: D3Link[] = [];

  const cx = 500, cy = 250;
  const saasType = corr.info?.saasType || 'SaaS';

  // Entity/User card node (top right area)
  const entityId = 'entity';
  nodes.push({
    id: entityId,
    type: 'card',
    position: { x: cx + 250, y: cy - 150 },
    data: {
      label: corr.entity,
      type: 'user' as NodeType,
      provider: (corr.cloudType?.toLowerCase() || 'gcp') as Provider,
      activity: 14,
      alertCount: 0,
      roleCount: 0,
      resourceCount: 0,
    },
  });
  d3Nodes.push({ id: entityId, x: cx + 250, y: cy - 150, isCard: true });

  // SaaS platform circular node (center)
  const saasId = 'saas-platform';
  nodes.push({
    id: saasId,
    type: 'saas',
    position: { x: cx, y: cy },
    data: {
      label: saasType.toUpperCase(),
      type: 'resource' as NodeType,
      saasName: saasType.toLowerCase(),
      risk: corr.severity === 'HIGH' || corr.severity === 'CRITICAL' ? 'high' : corr.severity === 'MEDIUM' ? 'medium' : 'low',
    } as SimpleNodeData,
  });
  d3Nodes.push({ id: saasId, x: cx, y: cy, isCard: false });

  // Connect entity to SaaS
  edges.push({ id: 'e-entity-saas', source: entityId, target: saasId, type: 'simple' });
  d3Links.push({ id: 'e-entity-saas', source: entityId, target: saasId });

  // Secrets/Tokens as circular nodes
  const secrets = corr.entityAssociated || [];
  secrets.forEach((secret, i) => {
    const sId = `secret-${i}`;
    const angle = Math.PI / 4 + (Math.PI / 2 * i) / Math.max(secrets.length - 1, 1);
    const x = cx + 200 * Math.cos(angle);
    const y = cy + 200 * Math.sin(angle);
    nodes.push({
      id: sId,
      type: 'circular',
      position: { x, y },
      data: { label: secret, type: 'token' as NodeType },
    });
    d3Nodes.push({ id: sId, x, y, isCard: false });
    edges.push({ id: `e-saas-secret-${i}`, source: saasId, target: sId, type: 'simple' });
    d3Links.push({ id: `e-saas-secret-${i}`, source: saasId, target: sId });
    // Also connect entity to secret
    edges.push({ id: `e-entity-secret-${i}`, source: entityId, target: sId, type: 'simple' });
    d3Links.push({ id: `e-entity-secret-${i}`, source: entityId, target: sId });
  });

  // Repositories from correlation map as card nodes
  const corrMap = corr.correlation || {};
  const repos = new Set<string>();
  Object.values(corrMap).forEach(secretMap => {
    Object.values(secretMap).forEach(repoList => {
      repoList.forEach(r => repos.add(r));
    });
  });

  let repoIdx = 0;
  repos.forEach(repo => {
    const rId = `repo-${repoIdx}`;
    const x = cx - 250;
    const y = cy - 80 + repoIdx * 160;
    nodes.push({
      id: rId,
      type: 'card',
      position: { x, y },
      data: {
        label: repo,
        type: 'resource' as NodeType,
        resourceType: 'repository',
        provider: (corr.cloudType?.toLowerCase() || 'gcp') as Provider,
      },
    });
    d3Nodes.push({ id: rId, x, y, isCard: true });
    edges.push({ id: `e-saas-repo-${repoIdx}`, source: saasId, target: rId, type: 'simple' });
    d3Links.push({ id: `e-saas-repo-${repoIdx}`, source: saasId, target: rId });
    repoIdx++;
  });

  return { nodes, edges, d3Nodes, d3Links };
}

function buildSaasGraph(saasData: SaaSData) {
  const nodes: Node<SimpleNodeData>[] = [];
  const edges: Edge[] = [];
  const d3Nodes: D3Node[] = [];
  const d3Links: D3Link[] = [];
  const cx = 400, cy = 200;

  nodes.push({
    id: 'saas',
    type: 'saas',
    position: { x: cx, y: cy },
    data: {
      label: saasData.name,
      type: 'resource' as NodeType,
      saasName: saasData.name,
      risk: saasData.risk,
    } as SimpleNodeData,
  });
  d3Nodes.push({ id: 'saas', x: cx, y: cy, isCard: false });

  const radius = 220;
  saasData.secrets.forEach((secret, i) => {
    const angle = (2 * Math.PI * i) / saasData.secrets.length - Math.PI / 2;
    const x = cx + radius * Math.cos(angle);
    const y = cy + radius * Math.sin(angle);
    nodes.push({
      id: `secret-${i}`,
      type: 'circular',
      position: { x, y },
      data: { label: secret, type: 'secret' as NodeType },
    });
    d3Nodes.push({ id: `secret-${i}`, x, y, isCard: false });
    edges.push({ id: `e-secret-${i}`, source: 'saas', target: `secret-${i}`, type: 'simple' });
    d3Links.push({ id: `e-secret-${i}`, source: 'saas', target: `secret-${i}` });
  });

  return { nodes, edges, d3Nodes, d3Links };
}

const NHICorrelationGraphInner = ({ serviceData, saasData, graphType = 'service', cloudSaasCorrelation }: NHICorrelationGraphProps) => {
  const simulationRef = useRef<ReturnType<typeof forceSimulation<D3Node>> | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const { initialNodes, initialEdges, d3Nodes, d3Links } = useMemo(() => {
    if (graphType === 'cloud-saas' && cloudSaasCorrelation) {
      const result = buildCloudSaasGraph(cloudSaasCorrelation);
      return { initialNodes: result.nodes, initialEdges: result.edges, d3Nodes: result.d3Nodes, d3Links: result.d3Links };
    }
    if (graphType === 'saas' && saasData) {
      const result = buildSaasGraph(saasData);
      return { initialNodes: result.nodes, initialEdges: result.edges, d3Nodes: result.d3Nodes, d3Links: result.d3Links };
    }
    if (serviceData) {
      const result = buildServiceGraph(serviceData);
      return { initialNodes: result.nodes, initialEdges: result.edges, d3Nodes: result.d3Nodes, d3Links: result.d3Links };
    }
    return { initialNodes: [], initialEdges: [], d3Nodes: [], d3Links: [] };
  }, [serviceData, saasData, graphType, cloudSaasCorrelation]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => {
    const cx = 500, cy = 250;
    const simNodes = d3Nodes.map(n => ({ ...n }));
    const simLinks = d3Links.map(l => ({ ...l }));

    const simulation = forceSimulation<D3Node>(simNodes)
      .force('link', forceLink<D3Node, D3Link>(simLinks)
        .id(d => d.id)
        .distance(220)
        .strength(0.3)
      )
      .force('charge', forceManyBody<D3Node>()
        .strength(-800)
        .distanceMax(500)
      )
      .force('collide', forceCollide<D3Node>()
        .radius(d => d.isCard ? 120 : 60)
        .strength(0.8)
      )
      .force('centerX', forceX<D3Node>(cx).strength(0.04))
      .force('centerY', forceY<D3Node>(cy).strength(0.04))
      .alphaDecay(0.02)
      .velocityDecay(0.3);

    simulationRef.current = simulation;

    const tick = () => {
      setNodes(currentNodes =>
        currentNodes.map(node => {
          const simNode = simNodes.find(n => n.id === node.id);
          if (simNode && simNode.x !== undefined && simNode.y !== undefined) {
            return { ...node, position: { x: simNode.x, y: simNode.y } };
          }
          return node;
        })
      );
      animationFrameRef.current = requestAnimationFrame(tick);
    };

    simulation.on('tick', () => { });
    tick();

    return () => {
      simulation.stop();
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [d3Nodes, d3Links, setNodes]);

  const onNodeDragStart = useCallback((_: React.MouseEvent, node: Node) => {
    if (simulationRef.current) {
      const simNode = simulationRef.current.nodes().find(n => n.id === node.id);
      if (simNode) { simNode.fx = node.position.x; simNode.fy = node.position.y; }
      simulationRef.current.alpha(0.3).restart();
    }
  }, []);

  const onNodeDrag = useCallback((_: React.MouseEvent, node: Node) => {
    if (simulationRef.current) {
      const simNode = simulationRef.current.nodes().find(n => n.id === node.id);
      if (simNode) { simNode.fx = node.position.x; simNode.fy = node.position.y; }
    }
  }, []);

  const onNodeDragStop = useCallback((_: React.MouseEvent, node: Node) => {
    if (simulationRef.current) {
      const simNode = simulationRef.current.nodes().find(n => n.id === node.id);
      if (simNode) { simNode.fx = null; simNode.fy = null; simNode.x = node.position.x; simNode.y = node.position.y; }
      simulationRef.current.alpha(0.5).restart();
    }
  }, []);

  return (
    <div className="w-full h-full relative rounded-xl overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          backgroundImage: `
            radial-gradient(circle at center, hsl(var(--muted-foreground) / 0.15) 1px, transparent 1px),
            linear-gradient(to right, hsl(var(--border) / 0.3) 1px, transparent 1px),
            linear-gradient(to bottom, hsl(var(--border) / 0.3) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px, 40px 40px, 40px 40px',
          boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.06), inset 0 -2px 8px rgba(0,0,0,0.06)',
        }}
      />
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeDragStart={onNodeDragStart}
        onNodeDrag={onNodeDrag}
        onNodeDragStop={onNodeDragStop}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        connectionMode={ConnectionMode.Loose}
        fitView
        fitViewOptions={{ padding: 0.3, maxZoom: 1.0, minZoom: 0.4 }}
        panOnDrag={true}
        zoomOnScroll={true}
        zoomOnPinch={true}
        zoomOnDoubleClick={false}
        nodesDraggable={true}
        nodesConnectable={false}
        elementsSelectable={true}
        minZoom={0.2}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
        className="bg-transparent"
      >
        <Background variant={BackgroundVariant.Dots} gap={40} size={1} color="transparent" />
      </ReactFlow>
    </div>
  );
};

export const NHICorrelationGraph = (props: NHICorrelationGraphProps) => {
  return (
    <ReactFlowProvider>
      <NHICorrelationGraphInner {...props} />
    </ReactFlowProvider>
  );
};
